import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { pick } from 'lodash';
import { shallow } from 'zustand/shallow';

import { getRegions } from '../../api/Rest/system';
import {
  JoyajoyImage,
  ListItemProps,
  SvgIcon,
} from '../../components/Molecules';
import { TrialAccountLogOutPopup } from '../../components/Popups';
import { AccountInfoTemplate } from '../../components/Templates/AccountSetting/AccountInfoTemplate';
import { NOT_APPLICABLE_STRING } from '../../constants/general';
import { Icons, IconType } from '../../constants/icons';
import { Regions, RegionsCode, WorldwideCode } from '../../constants/region';
import { SVGIconType } from '../../constants/svgIcon';
import {
  getWebPageCustomLink,
  getWebPageVariant,
  WebPageType,
} from '../../constants/webPageType';
import { usePopup, useShareData } from '../../context';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import { TranslationLocale } from '../../i18n';
import { Regions as TranslatedRegions } from '../../models/SystemStore/SystemStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { getLocaleText, getUiLocale } from '../../utils/i18n';
import { getRegionInfo } from '../../utils/region';

import styles from '../../components/Templates/AccountSetting/AccountInfoTemplate/AccountInfoTemplate.module.css';

const { deleteTrialAccount } = useSessionStore.getState();

interface AccountInfoScreenProps {
  webPageType: WebPageType;
}

const whiteToBlackPngStyle = {
  filter: 'brightness(0) sepia(100%) saturate(100%) hue-rotate(190deg)',
};

const AccountInfoScreen: React.FC<AccountInfoScreenProps> = ({
  webPageType,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { setSharedData } = useShareData();

  useEffect(() => {
    setSharedData(undefined);
  }, [setSharedData]);

  useRedirectEntry({ webPageType });

  const userId = useSessionStore((state) => state.activeUserId);
  const profile = useUserStore((state) => state.getProfile(userId ?? ''));

  const { username, email, phone } = useUserStore(
    (state) =>
      pick(state.getAccount(userId ?? ''), ['username', 'email', 'phone']),
    shallow,
  );

  const { name, region, isTrialUser } = useUserStore(
    (state) =>
      pick(state.getProfile(userId ?? ''), [
        'name',
        'profilePicture',
        'region',
        'avatar',
        'isTrialUser',
      ]),
    shallow,
  );

  const onClickName = useCallback(() => {
    navigate(
      getWebPageCustomLink(webPageType, '/settings/account/display-name'),
    );
  }, [navigate, webPageType]);

  const onClickUsername = useCallback(() => {
    navigate(getWebPageCustomLink(webPageType, '/settings/account/username'));
  }, [navigate, webPageType]);

  const onClickEmail = useCallback(() => {
    navigate(getWebPageCustomLink(webPageType, '/settings/account/email'));
  }, [navigate, webPageType]);

  const onClickPhoneNumber = useCallback(() => {
    navigate(
      getWebPageCustomLink(webPageType, '/settings/account/phone-number'),
    );
  }, [navigate, webPageType]);

  const onClickPassword = useCallback(() => {
    navigate(getWebPageCustomLink(webPageType, '/settings/account/password'));
  }, [navigate, webPageType]);

  const onClickRegions = useCallback(() => {
    navigate(getWebPageCustomLink(webPageType, '/settings/account/region'));
  }, [navigate, webPageType]);

  const onClickLanguage = useCallback(() => {
    navigate(
      getWebPageCustomLink(webPageType, '/settings/account/interface-language'),
    );
  }, [navigate, webPageType]);

  const arrowIcon = useMemo(
    () => (
      <SvgIcon
        name={SVGIconType.Arrow}
        color={'#808080'}
        width={15}
        height={15}
      />
    ),
    [],
  );

  const accountListData: ListItemProps[] = useMemo(() => {
    return [
      {
        icon: {
          resource: { uri: Icons[IconType.SettingDisplayName] },
          style: whiteToBlackPngStyle,
        },
        title: t('Display Name'),
        description: name || t('Displayed name not set'),
        rightComponent: arrowIcon,
        onPress: onClickName,
      },
      {
        icon: {
          resource: { uri: Icons[IconType.SettingUsername] },
          style: whiteToBlackPngStyle,
        },
        title: t('Username'),
        description: username || t('Username not set'),
        rightComponent: arrowIcon,
        onPress: onClickUsername,
      },
      {
        icon: { resource: { uri: Icons[IconType.Email] } },
        title: t('Email'),
        description: '',
        rightComponent: (
          <div className={styles.rightComponent}>
            {email?.isVerified && (
              <div className={styles.tickContainer}>
                <SvgIcon
                  name={getWebPageVariant(webPageType).circleTick}
                  width={24}
                  height={24}
                  color="white"
                />
              </div>
            )}
            {email?.address || t('Email not set')}
            {arrowIcon}
          </div>
        ),
        onPress: onClickEmail,
      },
      {
        icon: { resource: { uri: Icons[IconType.Phone] } },
        title: t('Phone Number'),
        description: '',
        rightComponent: (
          <div className={styles.rightComponent}>
            {phone?.phoneNumber || NOT_APPLICABLE_STRING}
            {arrowIcon}
          </div>
        ),
        onPress: onClickPhoneNumber,
      },
      {
        icon: { resource: { uri: Icons[IconType.Password] } },
        title: t('Password'),
        description: '******',
        rightComponent: arrowIcon,
        onPress: onClickPassword,
      },
    ];
  }, [
    arrowIcon,
    email?.address,
    email?.isVerified,
    name,
    onClickEmail,
    onClickName,
    onClickPassword,
    onClickPhoneNumber,
    onClickUsername,
    phone?.phoneNumber,
    t,
    username,
    webPageType,
  ]);

  const [translatedRegions, setTranslatedRegions] = useState<TranslatedRegions>(
    {},
  );

  useEffect(() => {
    const fetchTranslatedRegions = async () => {
      try {
        const response = await getRegions({
          locale: getUiLocale(i18n.language as TranslationLocale),
        });
        if (response.ok && response.data) {
          // remove the "Worldwide" option
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { worldwide, ...rest } = response.data.regions;
          setTranslatedRegions(rest);
        }
      } catch (error) {
        console.error('Error fetching translated regions:', error);
      }
    };

    void fetchTranslatedRegions();
  }, [i18n.language]);

  const getRegionImage = (regionId: RegionsCode | typeof WorldwideCode) => {
    if (regionId === WorldwideCode) {
      return Regions.find((r) => r.id === WorldwideCode)?.image || '';
    }
    return getRegionInfo(regionId).image || '';
  };

  const regionListData: ListItemProps[] = useMemo(
    () => [
      {
        icon: {
          resource: { uri: Icons[IconType.Earth] },
          style: whiteToBlackPngStyle,
        },
        title: t('Country / Region'),
        description: '',
        rightComponent: (
          <div className={styles.rightComponent}>
            <JoyajoyImage
              resource={{ uri: getRegionImage(region || WorldwideCode) }}
              style={{ width: 28, height: 28, marginRight: 3 }}
            />
            {translatedRegions[region || WorldwideCode]}
            {arrowIcon}
          </div>
        ),
        onPress: onClickRegions,
      },
      {
        icon: SVGIconType.HyperLink,
        title: t('Interface Language'),
        description: '',
        rightComponent: (
          <div className={styles.rightComponent}>
            {t(getLocaleText(getUiLocale(i18n.language as TranslationLocale)))}
            {arrowIcon}
          </div>
        ),
        onPress: onClickLanguage,
      },
    ],
    [
      arrowIcon,
      i18n.language,
      onClickLanguage,
      onClickRegions,
      region,
      t,
      translatedRegions,
    ],
  );

  const { showPopup, hidePopup } = usePopup();

  const onClickTrialLogout = useCallback(() => {
    const trialLogout = async () => {
      if (isTrialUser) {
        hidePopup();
        await deleteTrialAccount();
      }
    };

    showPopup(
      <TrialAccountLogOutPopup
        onPrimary={() => {
          trialLogout().catch(console.error);
        }}
        onCancel={hidePopup}
      />,
    );
  }, [hidePopup, isTrialUser, showPopup]);

  return (
    <AccountInfoTemplate
      profile={profile ?? undefined}
      accountListData={accountListData}
      regionListData={regionListData}
      onClickTrialLogout={onClickTrialLogout}
    />
  );
};

export default AccountInfoScreen;
